import React, { useState, useEffect } from 'react';
import { Receipt, Calculator, Send, DollarSign, Percent, AlertCircle, CheckCircle, XCircle, Stamp, Clock } from 'lucide-react';
import type { Deal, DealService, DealVariable } from '../../lib/types';
import { useDealVariableStore } from '../../lib/stores/dealVariableStore';
import { enhancedInvoiceQueueService } from '../../lib/services/enhancedInvoiceQueueService';
import { supabase } from '../../lib/supabase';

interface DealVariablesBillingSectionProps {
  deal: Deal;
  dealServices: DealService[];
  translations: any;
  onDealUpdate?: () => void;
}

interface ServiceVariableWithBase {
  variable: DealVariable;
  service: DealService;
  baseAmount?: number;
  calculatedAmount: number;
}

export function DealVariablesBillingSection({
  deal,
  dealServices,
  translations: t,
  onDealUpdate
}: DealVariablesBillingSectionProps) {
  const [serviceVariables, setServiceVariables] = useState<ServiceVariableWithBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [variablesInQueue, setVariablesInQueue] = useState<Set<string>>(new Set());

  const { fetchServiceVariables, updateServiceVariable, confirmVariable, unconfirmVariable } = useDealVariableStore();

  // Load variables already in queue for this deal
  useEffect(() => {
    const loadVariablesInQueue = async () => {
      try {
        // Query both invoice_queue and enhanced_invoice_queue for items with variable_id in metadata
        const [invoiceQueueResult, enhancedQueueResult] = await Promise.all([
          supabase
            .from('invoice_queue')
            .select('metadata')
            .eq('deal_id', deal.id)
            .not('metadata', 'is', null),
          supabase
            .from('enhanced_invoice_queue')
            .select('metadata')
            .eq('deal_id', deal.id)
            .not('metadata', 'is', null)
        ]);

        // Extract variable_ids from metadata
        const inQueueIds = new Set<string>();

        invoiceQueueResult.data?.forEach(item => {
          if (item.metadata?.variable_id) {
            inQueueIds.add(item.metadata.variable_id);
          }
        });

        enhancedQueueResult.data?.forEach(item => {
          if (item.metadata?.variable_id) {
            inQueueIds.add(item.metadata.variable_id);
          }
        });

        setVariablesInQueue(inQueueIds);
      } catch (error) {
        console.error('Error loading variables in queue:', error);
      }
    };

    if (deal.id) {
      loadVariablesInQueue();
    }
  }, [deal.id]);

  // Load ALL variables (both draft and confirmed) for all services
  useEffect(() => {
    const loadAllServiceVariables = async () => {
      if (!dealServices || dealServices.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const allVariables: ServiceVariableWithBase[] = [];

      try {
        for (const service of dealServices) {
          if (service.id) {
            await fetchServiceVariables(service.id);
            const variables = useDealVariableStore.getState().getServiceVariablesByServiceId(service.id);

            // Include ALL variables (both draft and confirmed)
            const serviceVars: ServiceVariableWithBase[] = variables.map(variable => ({
              variable,
              service,
              baseAmount: variable.type === 'percentage' ? (variable.base_amount || 0) : undefined,
              calculatedAmount: variable.type === 'fixed' ? variable.amount : (variable.base_amount ? (variable.base_amount * variable.amount) / 100 : 0)
            }));

            allVariables.push(...serviceVars);
          }
        }

        setServiceVariables(allVariables);
      } catch (error) {
        console.error('Error loading service variables:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllServiceVariables();
  }, [dealServices, fetchServiceVariables]);

  // Update calculated amount when base amount changes
  const updateBaseAmount = async (index: number, baseAmount: number) => {
    const updatedVariables = [...serviceVariables];
    const serviceVar = updatedVariables[index];

    if (serviceVar.variable.type === 'percentage') {
      serviceVar.baseAmount = baseAmount;
      serviceVar.calculatedAmount = (baseAmount * serviceVar.variable.amount) / 100;

      // Save to database
      try {
        await updateServiceVariable(serviceVar.variable.id, {
          base_amount: baseAmount
        });
      } catch (error) {
        console.error('Error saving base amount:', error);
      }
    }

    setServiceVariables(updatedVariables);
  };

  // Confirm variable
  const handleConfirmVariable = async (index: number) => {
    const serviceVar = serviceVariables[index];

    // Validation for percentage variables
    if (serviceVar.variable.type === 'percentage' && (!serviceVar.baseAmount || serviceVar.baseAmount <= 0)) {
      alert('Inserisci l\'importo base prima di confermare la variabile');
      return;
    }

    setProcessing(true);

    try {
      const confirmed = await confirmVariable(serviceVar.variable.id, serviceVar.calculatedAmount);
      if (confirmed) {
        // Update the variable status in local state instead of removing it
        const updatedVariables = [...serviceVariables];
        updatedVariables[index] = {
          ...updatedVariables[index],
          variable: {
            ...updatedVariables[index].variable,
            is_confirmed: true,
            confirmed_at: confirmed.confirmed_at,
            confirmed_by: confirmed.confirmed_by
          }
        };
        setServiceVariables(updatedVariables);
        // Refresh deal to update displayed total
        onDealUpdate?.();
      }
    } catch (error) {
      console.error('Error confirming variable:', error);
      alert('Errore nella conferma della variabile');
    } finally {
      setProcessing(false);
    }
  };

  // Unconfirm variable
  const handleUnconfirmVariable = async (index: number) => {
    const serviceVar = serviceVariables[index];

    const confirmMessage = `Sei sicuro di voler annullare la conferma della variabile "${serviceVar.variable.name}"?\n\n` +
      `Dettagli:\n` +
      `- Importo: €${serviceVar.calculatedAmount.toFixed(2)}\n` +
      `- Tipo: ${serviceVar.variable.is_internal_cost ? 'Costo Interno' : 'Variabile Fatturabile'}\n\n` +
      `Potrai modificare il valore e riconfermarla successivamente.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    setProcessing(true);

    try {
      const unconfirmed = await unconfirmVariable(serviceVar.variable.id);
      if (unconfirmed) {
        // Update the variable status in local state
        const updatedVariables = [...serviceVariables];
        updatedVariables[index] = {
          ...updatedVariables[index],
          variable: {
            ...updatedVariables[index].variable,
            is_confirmed: false,
            confirmed_at: undefined,
            confirmed_by: undefined
          }
        };
        setServiceVariables(updatedVariables);
        // Refresh deal to update displayed total
        onDealUpdate?.();
        alert('Variabile annullata con successo. Puoi ora modificarla e riconfermarla.');
      }
    } catch (error) {
      console.error('Error unconfirming variable:', error);
      alert('Errore nell\'annullamento della variabile');
    } finally {
      setProcessing(false);
    }
  };

  // Send confirmed variable to invoice queue
  const handleConfirmAndSendToQueue = async (index: number) => {
    const serviceVar = serviceVariables[index];

    // Block sending if deal is not accepted
    if (deal.status !== 'accepted') {
      alert('L\'offerta deve essere accettata prima di poter inviare variabili in coda di fatturazione');
      return;
    }

    // Validation for percentage variables
    if (serviceVar.variable.type === 'percentage' && (!serviceVar.baseAmount || serviceVar.baseAmount <= 0)) {
      alert('Inserisci l\'importo base prima di inviare in coda');
      return;
    }

    setProcessing(true);

    try {
      // First confirm the variable if not already confirmed
      if (!serviceVar.variable.is_confirmed) {
        const confirmed = await confirmVariable(serviceVar.variable.id, serviceVar.calculatedAmount);
        if (!confirmed) {
          throw new Error('Failed to confirm variable');
        }
      }

      // Then send to invoice queue with full FIC data
      await enhancedInvoiceQueueService.addVariableToQueue({
        dealId: deal.id,
        dealServiceId: serviceVar.service.id,
        variable: {
          id: serviceVar.variable.id,
          name: serviceVar.variable.name,
          description: serviceVar.variable.description,
          type: serviceVar.variable.type,
          amount: serviceVar.variable.amount,
          is_cost: serviceVar.variable.is_cost,
          is_vat_exempt: serviceVar.variable.is_vat_exempt,
          is_stamp_duty: serviceVar.variable.is_stamp_duty
        },
        calculatedAmount: serviceVar.calculatedAmount
      });

      alert('Variabile confermata e inviata in coda di fatturazione');

      // Update the variable status in local state (keep it visible, just mark as confirmed)
      const updatedVariables = [...serviceVariables];
      updatedVariables[index] = {
        ...updatedVariables[index],
        variable: {
          ...updatedVariables[index].variable,
          is_confirmed: true
        }
      };
      setServiceVariables(updatedVariables);

      // Add to variablesInQueue set to disable the button
      setVariablesInQueue(prev => new Set([...prev, serviceVar.variable.id]));

    } catch (error) {
      console.error('Error confirming and sending variable:', error);
      alert('Errore nell\'invio della variabile');
    } finally {
      setProcessing(false);
    }
  };

  // Don't render if no variables at all
  if (!loading && serviceVariables.length === 0) {
    return null;
  }

  const inQueueCount = serviceVariables.filter(v => variablesInQueue.has(v.variable.id)).length;
  const draftCount = serviceVariables.filter(v => !v.variable.is_confirmed && !variablesInQueue.has(v.variable.id)).length;
  const confirmedCount = serviceVariables.filter(v => v.variable.is_confirmed && !variablesInQueue.has(v.variable.id)).length;

  return (
    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-black/10 dark:border-gray-700 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Receipt className="h-5 w-5 text-black/60 dark:text-white/60" />
          <h3 className="text-lg font-medium text-black dark:text-white">
            Variabili offerta
          </h3>
          <div className="flex items-center gap-2">
            {draftCount > 0 && (
              <span className="bg-yellow-100 text-yellow-800 text-sm px-2 py-1 rounded-full">
                {draftCount} da confermare
              </span>
            )}
            {confirmedCount > 0 && (
              <span className="bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
                {confirmedCount} confermate
              </span>
            )}
            {inQueueCount > 0 && (
              <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                {inQueueCount} in coda
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-black/60 dark:text-white/60 text-sm">Caricamento variabili...</p>
        </div>
      ) : (
        <>
          {/* Variables List */}
          <div className="space-y-2">
            {serviceVariables.map((serviceVar, index) => {
              const isConfirmed = serviceVar.variable.is_confirmed;
              const isInQueue = variablesInQueue.has(serviceVar.variable.id);

              return (
                <div
                  key={`${serviceVar.service.id}-${serviceVar.variable.id}`}
                  className={`border rounded-lg p-4 ${
                    isInQueue
                      ? 'border-blue-200 bg-blue-50/30'
                      : isConfirmed
                        ? 'border-green-200 bg-green-50/30'
                        : 'border-yellow-200 bg-yellow-50/30'
                  }`}
                >
                  {/* Row 1: Variable Name, Service, Status Badge, Type Badge */}
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Variable Name */}
                      <span className="font-medium text-black dark:text-white text-sm truncate">
                        {serviceVar.variable.name}
                      </span>
                      {/* Service Name */}
                      <span className="text-xs text-black/50 dark:text-white/50 truncate hidden sm:inline">
                        • {serviceVar.service.service?.title || 'Servizio'}
                      </span>
                    </div>

                    {/* Badges Group */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Status Badge */}
                      {isInQueue ? (
                        <span className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                          <Clock className="h-3 w-3" />
                          <span>In coda</span>
                        </span>
                      ) : isConfirmed ? (
                        <span className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs">
                          <CheckCircle className="h-3 w-3" />
                          <span>Confermata</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs">
                          <AlertCircle className="h-3 w-3" />
                          <span>Bozza</span>
                        </span>
                      )}

                      {/* Type Badge */}
                      {serviceVar.variable.type === 'percentage' ? (
                        <span className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                          <Percent className="h-3 w-3" />
                          <span>{serviceVar.variable.amount}%</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs">
                          <DollarSign className="h-3 w-3" />
                          <span>Fisso</span>
                        </span>
                      )}

                      {/* Internal Cost Badge */}
                      {serviceVar.variable.is_internal_cost && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                          Costo Interno
                        </span>
                      )}

                      {/* Stamp Duty Badge */}
                      {serviceVar.variable.is_stamp_duty && (
                        <span className="flex items-center gap-1 bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full">
                          <Stamp className="h-3 w-3" />
                          <span>Bollo</span>
                        </span>
                      )}

                      {/* Stamp Duty Art. 15 Badge */}
                      {serviceVar.variable.is_stamp_duty_art15 && (
                        <span className="flex items-center gap-1 bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full">
                          <Stamp className="h-3 w-3" />
                          <span>Art. 15</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Base Amount Input, Calculated Amount, Action Buttons */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Service name on mobile */}
                    <span className="text-xs text-black/50 dark:text-white/50 sm:hidden w-full mb-1">
                      {serviceVar.service.service?.title || 'Servizio'}
                    </span>

                    {/* Base Amount Input (only for percentage) */}
                    {serviceVar.variable.type === 'percentage' && (
                      <div className="flex items-center gap-2">
                        <div className="relative w-24 sm:w-32">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <span className="text-black/60 dark:text-white/60 text-xs">€</span>
                          </div>
                          <input
                            type="number"
                            value={serviceVar.baseAmount || ''}
                            onChange={(e) => updateBaseAmount(index, parseFloat(e.target.value) || 0)}
                            placeholder="Importo base"
                            disabled={isConfirmed}
                            className="w-full pl-6 pr-2 py-1.5 text-xs border border-black/20 dark:border-white/20 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <div className="flex items-center gap-1 text-xs text-black/60 dark:text-white/60">
                          <Calculator className="h-3 w-3" />
                          <span>=</span>
                        </div>
                      </div>
                    )}

                    {/* Calculated Amount */}
                    <div className="text-right min-w-[80px]">
                      <div className="font-semibold text-black dark:text-white text-sm">
                        {new Intl.NumberFormat('it-IT', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(serviceVar.calculatedAmount)}
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {isInQueue ? (
                        // Variable is already in queue - show disabled state
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-200 text-gray-600 rounded cursor-not-allowed">
                          <Clock className="h-3 w-3" />
                          <span>Già in coda</span>
                        </span>
                      ) : isConfirmed ? (
                        <>
                          <button
                            onClick={() => handleUnconfirmVariable(index)}
                            disabled={processing}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Annulla conferma per modificare la variabile"
                          >
                            <XCircle className="h-3 w-3" />
                            <span>Annulla</span>
                          </button>
                          <button
                            onClick={() => handleConfirmAndSendToQueue(index)}
                            disabled={processing}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#0f6cbd] text-white rounded hover:bg-[#0a5ba8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Send className="h-3 w-3" />
                            <span>Invia in Coda</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleConfirmVariable(index)}
                            disabled={processing}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <CheckCircle className="h-3 w-3" />
                            <span>Conferma</span>
                          </button>

                          <button
                            onClick={() => handleConfirmAndSendToQueue(index)}
                            disabled={processing}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#0f6cbd] text-white rounded hover:bg-[#0a5ba8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Send className="h-3 w-3" />
                            <span>Invia in Coda</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info Message */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 Conferma le variabili per includerle nel calcolo delle provvigioni. Puoi anche inviarle direttamente in coda di fatturazione.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
